import React, { FC, useCallback, useMemo } from 'react';
import { Box, Divider, Tab, Tabs, useTheme } from '@mui/material';
import {
  getSettingsViewActiveTab,
  settingsViewOnChangeActiveTab,
} from '../store/settingsViewSlice';
import { RootState } from 'src/store';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { SettingsTabs } from '../store/settingsViewSlice/types';
import { Option } from 'src/viewsTypes';
import IntegrationsIcon from '@mui/icons-material/CardMembershipOutlined';
import PersonIcon from '@mui/icons-material/AccountCircleOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SwipeableViews from 'react-swipeable-views';
import { transition } from 'src/theme';
import SettingsProfile from './Profile';

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const settingsOptions: Option<SettingsTabs>[] = [
  {
    value: SettingsTabs.Profile,
    label: 'פרופיל',
    Icon: PersonIcon,
    Component: <SettingsProfile />,
  },
  {
    value: SettingsTabs.Subscriptions,
    label: 'המנוי שלי',
    Icon: IntegrationsIcon,
    Component: <Box>אינטגרציות</Box>,
  },
  {
    value: SettingsTabs.General,
    label: 'הגדרות',
    Icon: SettingsIcon,
    Component: <Box>כללי</Box>,
  },
];

const Settings: FC<Props> = ({ activeTab, updateActiveTab }) => {
  const theme = useTheme();
  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: SettingsTabs) => {
      if (newValue !== activeTab) {
        updateActiveTab(newValue);
      }
    },
    [activeTab, updateActiveTab],
  );

  const Content = useMemo(() => {
    return settingsOptions.map((option, index) => {
      const InnerContent = option.Component ? option.Component : null;
      return (
        <Box
          key={`${option.value}-${index}`}
          component="span"
          width="100%"
          display="flex"
          sx={{
            height: `calc(100vh - ${theme.sizes.general.topBar.height}px)`,
            justifyContent: 'flex-end',
          }}
        >
          {InnerContent}
        </Box>
      );
    });
  }, [settingsOptions]);

  const currTabIndex = useMemo(() => {
    return (
      settingsOptions.findIndex((option) => option.value === activeTab) ?? 0
    );
  }, [activeTab, settingsOptions]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: 'background.paper',
        display: 'flex',
        height: `calc(100vh - ${theme.sizes.general.topBar.height}px)`,
      }}
    >
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={activeTab}
        onChange={handleChange}
        aria-label="Settings tabs"
        sx={{ borderRight: 1, borderColor: 'divider' }}
      >
        {settingsOptions.map(({ value, label, Icon }) => (
          <Tab
            key={value}
            label={label}
            value={value}
            icon={Icon ? <Icon /> : undefined}
            sx={{ px: ({ spacing }) => `${spacing(1.5)} !important` }}
          />
        ))}
      </Tabs>
      <Divider orientation="vertical" flexItem />
      <SwipeableViews
        axis="y"
        index={currTabIndex}
        style={{
          overflow: 'hidden',
          display: 'flex',
          height: `calc(100vh - ${theme.sizes.general.topBar.height}px)`,
          flex: 1,
          padding: theme.spacing(2),
          transition: transition(),
        }}
        containerStyle={{
          width: '100%',
          display: 'flex',
          transition: transition(),
        }}
        cellPadding={0}
        slideStyle={{
          overflow: 'hidden',
          display: 'flex',
        }}
        enableMouseEvents
        resistance
        animateTransitions
      >
        {Content}
      </SwipeableViews>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeTab: getSettingsViewActiveTab(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateActiveTab: (activeTab: SettingsTabs) =>
    dispatch(settingsViewOnChangeActiveTab(activeTab)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
